import { useState, useEffect, useCallback } from 'react';

// Configuration pour le bot Python existant
const API_BASE_URL = 'http://localhost:8000';
const WS_URL = 'ws://localhost:8000/ws';

export const useBotData = () => {
  const [botData, setBotData] = useState({
    status: 'connecting',
    balance: { total: 0, free: 0, used: 0 },
    position: null,
    profits: { '15s': 0, '1m': 0, '5m': 0 },
    dca_count: 0,
    micro_dca_count: 0,
    max_dca: 5,
    max_micro_dca: 5,
    trades_today: 0,
    market_regime: 'normal',
    volatility: 0,
    logs: [],
    entry_history: [],
    transaction_history: [],
    metrics: {}
  });

  const [isConnected, setIsConnected] = useState(false);
  const [ws, setWs] = useState(null);

  // Connexion WebSocket vers le bot Python
  useEffect(() => {
    const connectWebSocket = () => {
      try {
        console.log('🔗 Tentative de connexion au bot Python...');
        const websocket = new WebSocket(WS_URL);
        
        websocket.onopen = () => {
          console.log('🟢 WebSocket connecté au bot Python');
          setIsConnected(true);
          setWs(websocket);
        };

        websocket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            
            switch (message.type) {
              case 'initial_data':
              case 'bot_update':
                setBotData(prevData => ({
                  ...prevData,
                  ...message.data,
                  lastUpdate: new Date().toISOString()
                }));
                // Log seulement toutes les 20 mises à jour pour éviter le spam
                if (Math.random() < 0.05) {
                  console.log('📊 Données bot mises à jour:', message.data);
                }
                break;
              
              case 'new_log':
                setBotData(prevData => ({
                  ...prevData,
                  logs: [...prevData.logs.slice(-49), message.data]
                }));
                break;
              
              case 'trade_executed':
                console.log('💰 Trade exécuté:', message.data);
                setBotData(prevData => ({
                  ...prevData,
                  transaction_history: [message.data, ...prevData.transaction_history.slice(0, 49)]
                }));
                break;
              
              default:
                setBotData(prevData => ({
                  ...prevData,
                  ...message.data,
                  lastUpdate: new Date().toISOString()
                }));
            }
          } catch (error) {
            console.error('Erreur parsing WebSocket:', error);
          }
        };

        websocket.onclose = () => {
          console.log('🔴 WebSocket déconnecté du bot Python');
          setIsConnected(false);
          setWs(null);
          // Reconnexion automatique après 3 secondes
          setTimeout(connectWebSocket, 3000);
        };

        websocket.onerror = (error) => {
          console.error('❌ Erreur WebSocket:', error);
          setIsConnected(false);
        };

      } catch (error) {
        console.error('❌ Erreur connexion WebSocket:', error);
        setTimeout(connectWebSocket, 3000);
      }
    };

    connectWebSocket();

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  // Récupérer le statut du bot
  const fetchBotStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bot/status`);
      const data = await response.json();
      
      if (data.success) {
        setBotData(prevData => ({
          ...prevData,
          ...data.data,
          lastUpdate: new Date().toISOString()
        }));
        console.log('✅ Statut bot récupéré');
      }
    } catch (error) {
      console.error('❌ Erreur fetch status:', error);
    }
  }, []);

  // Contrôler le bot (start, stop, pause)
  const controlBot = useCallback(async (action) => {
    try {
      console.log(`🎮 Contrôle bot: ${action}`);
      const response = await fetch(`${API_BASE_URL}/api/bot/control/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log(`✅ Action ${action} exécutée avec succès`);
        return true;
      } else {
        console.error(`❌ Erreur action ${action}:`, data.error);
        return false;
      }
    } catch (error) {
      console.error(`❌ Erreur API ${action}:`, error);
      return false;
    }
  }, []);

  // Récupérer les métriques détaillées
  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bot/metrics`);
      const data = await response.json();
      
      if (data.success) {
        setBotData(prevData => ({
          ...prevData,
          metrics: data.data
        }));
      }
    } catch (error) {
      console.error('❌ Erreur fetch metrics:', error);
    }
  }, []);

  // Fallback: récupération périodique si WebSocket échoue
  useEffect(() => {
    if (!isConnected) {
      console.log('⚠️ WebSocket déconnecté, utilisation du polling');
      const interval = setInterval(fetchBotStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [isConnected, fetchBotStatus]);

  // Calculer les données du portefeuille pour l'interface
  const getPortfolioData = useCallback(() => {
    const totalProfits = (botData.profits?.['15s'] || 0) + 
                        (botData.profits?.['1m'] || 0) + 
                        (botData.profits?.['5m'] || 0);
    
    const totalValue = (botData.balance?.total || 0);
    const positionValue = botData.position ? 
      (botData.position.size || 0) * (botData.position.current_price || 0) : 0;
    
    return {
      balance: totalValue,
      usdcBalance: botData.balance?.free || 0,
      totalValue: totalValue + positionValue,
      pnl: (botData.position?.pnl || 0) + totalProfits,
      pnlPercent: botData.position?.pnl_percent || 0,
      scalpingProfits: totalProfits,
      position: botData.position,
      isRunning: botData.status === 'running',
      lastUpdate: botData.lastUpdate || new Date().toISOString()
    };
  }, [botData]);

  return {
    botData,
    isConnected,
    controlBot,
    refreshData: fetchBotStatus,
    fetchMetrics,
    getPortfolioData
  };
};
